import classNames from 'classnames';
import styles from '../styles/pages/Search.module.css';
import { MobileLayout } from 'components/mobile/MobileLayout/MobileLayout';
import {
  ChangeEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useBreakpoint } from 'hooks/useBreakpoint';
import { Navigate } from 'react-router-dom';
import { useExternalNavigate } from 'hooks/useExternalNavigate';
import { FORZZA_MOBILE_RESULTS_URL } from 'config/constants';
import { searchEventsByTeamName } from 'services/api/sportBetsService';
import { ThemeIcon } from 'components/ThemeIcon/ThemeIcon';
import { SearchResults } from 'components/mobile/SearchResults/SearchResults';

const SEARCH_DEBOUNCE_MS = 350;
const MIN_SEARCH_QUERY_LENGTH = 2;

enum SearchType {
  RegularSportsOffer = 'regular-sports',
  Results = 'results',
  LiveSportsOffer = 'live-sports',
}

export const Search = () => {
  const [expanded, setExpanded] = useState(true);
  const [selectedSearchType, setSelectedSearchType] = useState<string>(
    SearchType.RegularSportsOffer
  );
  const [searchResults, setSearchResults] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchedType, setSearchedType] = useState<string>('');
  const [emptySearchQuery, setEmptySearchQuery] = useState<string | null>(null);
  const { t } = useTranslation();
  const { navigate } = useExternalNavigate(FORZZA_MOBILE_RESULTS_URL, '_blank');
  const { isMobile } = useBreakpoint();

  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchSeqRef = useRef(0);
  const searchTypeRef = useRef(selectedSearchType);

  useEffect(() => {
    searchTypeRef.current = selectedSearchType;
  }, [selectedSearchType]);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  const invalidatePendingSearch = useCallback(() => {
    searchSeqRef.current += 1;
  }, []);

  const executeSearch = useCallback(async (raw: string) => {
    const type = searchTypeRef.current;
    if (type === SearchType.Results) return;
    const q = raw.trim();
    if (q.length < MIN_SEARCH_QUERY_LENGTH) {
      setSearchResults(null);
      setEmptySearchQuery(null);
      return;
    }
    const mySeq = ++searchSeqRef.current;
    try {
      const onlyLive = type === SearchType.LiveSportsOffer;
      const results = await searchEventsByTeamName(q, onlyLive, true);
      if (mySeq !== searchSeqRef.current) return;
      if (results && results.size > 0) {
        setSearchResults(results);
        setSearchedType(type);
        setEmptySearchQuery(null);
      } else {
        setSearchResults(null);
        setEmptySearchQuery(q);
      }
    } catch {
      if (mySeq !== searchSeqRef.current) return;
      setSearchResults(null);
      setEmptySearchQuery(q);
    }
  }, []);

  const scheduleSearchFromInput = (value: string) => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }

    const trimmed = value.trim();

    if (searchTypeRef.current === SearchType.Results) {
      setSearchResults(null);
      setEmptySearchQuery(null);
      return;
    }

    if (trimmed.length === 0) {
      invalidatePendingSearch();
      setSearchResults(null);
      setEmptySearchQuery(null);
      return;
    }

    if (trimmed.length < MIN_SEARCH_QUERY_LENGTH) {
      invalidatePendingSearch();
      setSearchResults(null);
      setEmptySearchQuery(null);
      return;
    }

    searchDebounceRef.current = setTimeout(() => {
      searchDebounceRef.current = null;
      const latest = searchInputRef.current?.value ?? value;
      void executeSearch(latest);
    }, SEARCH_DEBOUNCE_MS);
  };

  const handleSearchTermChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
    scheduleSearchFromInput(value);
  };

  const handleSearchTypeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const v = event.target.value;
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
    invalidatePendingSearch();
    searchTypeRef.current = v;
    setSelectedSearchType(v);
    setSearchResults(null);
    setEmptySearchQuery(null);
    if (v !== SearchType.Results) {
      const q = searchTerm.trim();
      if (q.length >= MIN_SEARCH_QUERY_LENGTH) {
        void executeSearch(searchTerm);
      }
    }
  };

  const handleInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const q = searchTerm.trim();
    if (selectedSearchType === SearchType.Results) {
      if (q.length >= MIN_SEARCH_QUERY_LENGTH) {
        navigate();
      }
      return;
    }
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
    if (q.length >= MIN_SEARCH_QUERY_LENGTH) {
      void executeSearch(searchTerm);
    }
  };

  if (!isMobile) {
    return <Navigate to="/" />;
  }

  const showSportsLiveEmpty =
    emptySearchQuery !== null &&
    !searchResults &&
    selectedSearchType !== SearchType.Results;

  return (
    <MobileLayout pageTitle={t('search_label')}>
      <div
        className={styles.searchHeader}
        onClick={() => setExpanded(!expanded)}
      >
        <div
          className={classNames(
            styles.icon,
            expanded ? styles.minusSign : styles.plusSign
          )}
        />
        {t('search_label')}
      </div>
      {expanded && (
        <>
          <div className={styles.searchTypeWrapper}>
            <div>{t('select_search_type_label')}:</div>
            <select
              value={selectedSearchType}
              onChange={handleSearchTypeChange}
            >
              <option value={SearchType.RegularSportsOffer}>
                {t('regular_sports_offer_label')}
              </option>
              <option value={SearchType.Results}>{t('results_label')}</option>
              <option value={SearchType.LiveSportsOffer}>
                {t('live_sports_offer_label')}
              </option>
            </select>
          </div>
          <div className={styles.searchHint}>
            <ThemeIcon icon="info-circle" alt="" variant="inline" />
            {selectedSearchType === SearchType.Results
              ? t('search_hint_results_type')
              : t('search_hint_message')}
          </div>
          <div className={styles.searchTermWrapper}>
            <div>{t('type_search_term_label')}:</div>
            <input
              ref={searchInputRef}
              type="text"
              placeholder={t('type_search_term_label')}
              value={searchTerm}
              onChange={handleSearchTermChange}
              onKeyDown={handleInputKeyDown}
              autoComplete="off"
              enterKeyHint={
                selectedSearchType === SearchType.Results ? 'go' : 'search'
              }
            />
          </div>
        </>
      )}
      {showSportsLiveEmpty && (
        <div className={styles.emptySearchInline}>
          <ThemeIcon icon="info-circle" alt="" variant="inline" />
          <span>
            {t('no_search_results').replace('{search_query}', emptySearchQuery)}
          </span>
        </div>
      )}
      {searchResults && (
        <SearchResults
          events={searchResults}
          liveSearch={searchedType === SearchType.LiveSportsOffer}
        />
      )}
    </MobileLayout>
  );
};
